import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { NEW_API_SERVER } from "src/components/Auth/AuthGuard";
import { satisfiesPermissionRequirement } from "./permissionMap";

const PermissionContext = createContext(null);

/** Coerces the response into a plain { permissionName: boolean } map. */
function normalizePermissionPayload(payload) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        throw new Error("Invalid permissions response");
    }

    return Object.freeze(
        Object.fromEntries(
            Object.entries(payload).map(([name, value]) => [name, Boolean(value)])
        )
    );
}

export function PermissionProvider({ children }) {
    const hasToken = () => Boolean(Cookies.get("Authorization"));

    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(hasToken);
    const [error, setError] = useState(null);

    // Lets a stale in-flight request be discarded instead of overwriting a
    // newer result — relevant on login, logout and React StrictMode remounts.
    const requestIdRef = useRef(0);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const clearPermissions = useCallback(() => {
        requestIdRef.current += 1; // invalidate anything in flight
        setPermissions(null);
        setError(null);
        setLoading(false);
    }, []);

    const refreshPermissions = useCallback(async () => {
        if (!hasToken()) {
            clearPermissions();
            return null;
        }

        const requestId = ++requestIdRef.current;
        const isCurrent = () => mountedRef.current && requestIdRef.current === requestId;

        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${NEW_API_SERVER()}users/me/permissions`
            );

            const nextPermissions = normalizePermissionPayload(response.data);

            if (!isCurrent()) return nextPermissions;

            setPermissions(nextPermissions);
            setError(null);
            return nextPermissions;
        } catch (requestError) {
            if (!isCurrent()) return null;

            const status = requestError?.response?.status;

            // Fail closed in every failure case: an empty permission set is
            // the only safe assumption when we cannot verify anything.
            setPermissions(null);

            if (status === 401 || status === 403) {
                // Not an error state — the session is simply gone, and
                // AuthGuard is responsible for the redirect.
                setError(null);
            } else {
                console.error("Could not load user permissions", requestError);
                setError(requestError);
            }

            return null;
        } finally {
            if (isCurrent()) setLoading(false);
        }
    }, [clearPermissions]);

    useEffect(() => {
        refreshPermissions();
    }, [refreshPermissions]);

    const hasPermission = useCallback(
        (permissionName) =>
            Boolean(permissionName && permissions?.[permissionName] === true),
        [permissions]
    );

    const canAccess = useCallback(
        (requirement) =>
            satisfiesPermissionRequirement(requirement, hasPermission),
        [hasPermission]
    );

    const value = useMemo(
        () => ({
            permissions,
            loading,
            error,
            /** true once a decision can be trusted (loaded, or no session) */
            ready: !loading,
            hasPermission,
            canAccess,
            refreshPermissions,
            clearPermissions,
        }),
        [
            permissions,
            loading,
            error,
            hasPermission,
            canAccess,
            refreshPermissions,
            clearPermissions,
        ]
    );

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
}

export function usePermissions() {
    const context = useContext(PermissionContext);

    if (!context) {
        throw new Error("usePermissions must be used inside PermissionProvider");
    }

    return context;
}
